import jsPDF from 'jspdf';
import { BusCheckoutData, BusPassenger } from '../interfaces/BusCheckout';
import logoSvg from '../assets/images/logo.svg';

async function loadLogoBase64(): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 740;
      canvas.height = 140;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, 740, 140);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = logoSvg;
  });
}

const BLUE = '#1E90FF';
const DARK = '#0f2744';
const GRAY = '#64748b';
const LIGHT_GRAY = '#f1f5f9';
const WHITE = '#ffffff';
const ORANGE = '#ea580c';
const W = 210;
const M = 10;

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function slugToCity(slug: string): string {
  if (!slug) return '—';
  const map: Record<string, string> = {
    'sao-paulo': 'São Paulo', 'rio-de-janeiro': 'Rio de Janeiro',
    'belo-horizonte': 'Belo Horizonte', 'porto-alegre': 'Porto Alegre',
    'curitiba': 'Curitiba', 'salvador': 'Salvador', 'fortaleza': 'Fortaleza',
    'recife': 'Recife', 'manaus': 'Manaus', 'belem': 'Belém',
    'goiania': 'Goiânia', 'florianopolis': 'Florianópolis', 'vitoria': 'Vitória',
    'natal': 'Natal', 'maceio': 'Maceió', 'joao-pessoa': 'João Pessoa',
    'teresina': 'Teresina', 'campo-grande': 'Campo Grande', 'cuiaba': 'Cuiabá',
    'porto-velho': 'Porto Velho', 'macapa': 'Macapá', 'boa-vista': 'Boa Vista',
    'rio-branco': 'Rio Branco', 'palmas': 'Palmas', 'aracaju': 'Aracaju',
    'sao-luis': 'São Luís', 'campinas': 'Campinas', 'santos': 'Santos',
    'ribeirao-preto': 'Ribeirão Preto', 'uberlandia': 'Uberlândia',
    'londrina': 'Londrina', 'joinville': 'Joinville', 'juiz-de-fora': 'Juiz de Fora',
    'sorocaba': 'Sorocaba', 'feira-de-santana': 'Feira de Santana',
    'sao-jose-dos-campos': 'São José dos Campos', 'osasco': 'Osasco',
    'contagem': 'Contagem', 'aparecida-de-goiania': 'Aparecida de Goiânia',
  };
  return map[slug] ?? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtDate(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtTime(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function fmtCurrency(v: string | null) {
  if (!v) return 'R$ 0,00';
  return parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Linha tracejada vertical (separador perfurado entre main e stub) */
function verticalDash(doc: jsPDF, x: number, yStart: number, yEnd: number) {
  doc.setDrawColor(...rgb(GRAY));
  doc.setLineWidth(0.3);
  const seg = 2, gap = 1.5;
  let yy = yStart;
  while (yy < yEnd) {
    doc.line(x, yy, x, Math.min(yy + seg, yEnd));
    yy += seg + gap;
  }
  // semicírculos nas bordas
  doc.setFillColor(240, 240, 240);
  doc.circle(x, yStart, 2.5, 'F');
  doc.circle(x, yEnd, 2.5, 'F');
}

/**
 * Ticket no estilo boarding pass:
 * [ parte principal branca (esquerda) | stub azul (direita) ]
 */
function ticketBlock(
  doc: jsPDF,
  y: number,
  tipo: 'IDA' | 'VOLTA',
  accentColor: string,
  logoB64: string | null,
  d: {
    cidadeOrigem: string; cidadeDestino: string;
    origemSlug: string; destinoSlug: string;
    partida: string; chegada: string;
    empresa: string; assento: string; veiculo: string;
    valor: string; pedidoId: string; passageiro: string;
  },
): number {
  const TW = W - M * 2;
  const TH = 95;
  const STUB = 50;
  const MAIN = TW - STUB;
  const tx = M;
  const stubX = tx + MAIN;

  // ── Sombra ──────────────────────────────────────────────────────────────
  doc.setFillColor(200, 215, 235);
  doc.roundedRect(tx + 1, y + 1, TW, TH, 3, 3, 'F');

  // ── Fundo principal (branco) ─────────────────────────────────────────────
  doc.setFillColor(...rgb(WHITE));
  doc.roundedRect(tx, y, TW, TH, 3, 3, 'F');

  // ── Stub direito (azul sólido) ───────────────────────────────────────────
  doc.setFillColor(...rgb(accentColor));
  doc.rect(stubX, y, STUB - 3, TH, 'F');
  doc.roundedRect(stubX + STUB - 6, y, 6, TH, 3, 3, 'F');
  doc.rect(stubX + STUB - 6, y, 3, TH, 'F');

  // Borda do ticket
  doc.setDrawColor(220, 230, 245);
  doc.setLineWidth(0.4);
  doc.roundedRect(tx, y, TW, TH, 3, 3, 'D');

  // ── Separador perfurado vertical ─────────────────────────────────────────
  verticalDash(doc, stubX, y, y + TH);

  // ════════════════════════════════════════════════════════════════════════
  // PARTE PRINCIPAL (esquerda)
  // ════════════════════════════════════════════════════════════════════════
  const px = tx + 4;
  const HDR_H = 13;

  // Faixa de cabeçalho azul
  doc.setFillColor(...rgb(accentColor));
  doc.rect(tx, y, MAIN, HDR_H, 'F');
  doc.roundedRect(tx, y, 6, HDR_H, 3, 3, 'F');
  doc.rect(tx + 3, y, 3, HDR_H, 'F');

  // Logo no cabeçalho
  if (logoB64) {
    doc.addImage(logoB64, 'PNG', px, y + 2, 42, 8);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('999 Viagens', px, y + 9);
  }

  // Badge IDA/VOLTA
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(stubX - 30, y + 3, 26, 7, 2, 2, 'F');
  doc.setTextColor(...rgb(accentColor));
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text(tipo === 'IDA' ? '> IDA' : '< VOLTA', stubX - 17, y + 8, { align: 'center' });

  // ── CARTÃO DE EMBARQUE + empresa ─────────────────────────────────────────
  doc.setTextColor(...rgb(DARK));
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('CARTÃO DE EMBARQUE', px, y + HDR_H + 7);

  // Empresa em destaque
  doc.setFillColor(...rgb(accentColor));
  const empLabel = (d.empresa || 'Ônibus').toUpperCase();
  const empW = doc.getTextWidth(empLabel) + 6;
  doc.roundedRect(px, y + HDR_H + 9, empW, 6, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.text(empLabel, px + 3, y + HDR_H + 13.5);

  // ── Linha divisória ───────────────────────────────────────────────────────
  const div1Y = y + HDR_H + 18;
  doc.setDrawColor(230, 235, 245);
  doc.setLineWidth(0.3);
  doc.line(px, div1Y, stubX - 4, div1Y);

  // ── Infos: passageiro, pedido, data ──────────────────────────────────────
  const row1Y = div1Y + 7;
  const infoItems = [
    { label: 'PASSAGEIRO', value: d.passageiro },
    { label: 'PEDIDO', value: `#${d.pedidoId}` },
    { label: 'DATA', value: fmtDate(d.partida) },
  ];
  const colW = (MAIN - 8) / infoItems.length;
  infoItems.forEach((item, i) => {
    const cx = px + i * colW;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...rgb(GRAY));
    doc.text(item.label, cx, row1Y);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...rgb(DARK));
    doc.text(doc.splitTextToSize(item.value, colW - 2)[0], cx, row1Y + 6);
  });

  // ── Linha divisória 2 ─────────────────────────────────────────────────────
  const div2Y = row1Y + 12;
  doc.setDrawColor(230, 235, 245);
  doc.setLineWidth(0.3);
  doc.line(px, div2Y, stubX - 4, div2Y);

  // ── Rota: ORIGEM → DESTINO em layout vertical ────────────────────────────
  const mainContentW = MAIN - 8;
  let ry = div2Y + 6;

  // EMBARQUE label + cidade origem
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...rgb(GRAY));
  doc.text('EMBARQUE', px, ry);
  ry += 5;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rgb(accentColor));
  doc.text(doc.splitTextToSize(d.cidadeOrigem.toUpperCase(), mainContentW)[0], px, ry);
  ry += 5;

  // Seta
  doc.setDrawColor(...rgb(accentColor));
  doc.setLineWidth(0.5);
  doc.line(px, ry, px + 10, ry);
  doc.setFillColor(...rgb(accentColor));
  doc.triangle(px + 10, ry - 1.5, px + 10, ry + 1.5, px + 13, ry, 'F');
  ry += 5;

  // DESEMBARQUE label + cidade destino
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...rgb(GRAY));
  doc.text('DESEMBARQUE', px, ry);
  ry += 5;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rgb(accentColor));
  doc.text(doc.splitTextToSize(d.cidadeDestino.toUpperCase(), mainContentW)[0], px, ry);
  ry += 6;

  // Horário e assento
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...rgb(GRAY));
  doc.text(`${fmtTime(d.partida)} → ${fmtTime(d.chegada)}  |  Assento: ${d.assento || '—'}  |  ${d.veiculo || '—'}`, px, ry);

  // ════════════════════════════════════════════════════════════════════════
  // STUB DIREITO (azul)
  // ════════════════════════════════════════════════════════════════════════
  const sx = stubX + 4;
  const sMaxW = STUB - 8;

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('PASSAGEM DE ÔNIBUS', sx + sMaxW / 2, y + 9, { align: 'center' });

  // Passageiro
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 230, 255);
  doc.text('PASSAGEIRO', sx, y + 19);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(doc.splitTextToSize(d.passageiro, sMaxW)[0], sx, y + 25);

  // Horário grande
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 230, 255);
  doc.text('HORÁRIO', sx, y + 33);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(fmtTime(d.partida), sx, y + 42);

  // Origem / Destino
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 230, 255);
  doc.text('ORIGEM', sx, y + 50);
  doc.text('DESTINO', sx + sMaxW / 2, y + 50);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(d.cidadeOrigem.split(' ')[0].toUpperCase(), sx, y + 56);
  doc.text(d.cidadeDestino.split(' ')[0].toUpperCase(), sx + sMaxW / 2, y + 56);

  // Data e assento
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 230, 255);
  doc.text('DATA', sx, y + 64);
  doc.text('ASSENTO', sx + sMaxW / 2, y + 64);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(fmtDate(d.partida), sx, y + 70);
  doc.text(d.assento || '—', sx + sMaxW / 2, y + 70);

  // Valor no rodapé do stub
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 230, 255);
  doc.text('VALOR', sx, y + 77);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(d.valor, sx, y + TH - 3);

  return y + TH + 5;
}

function passengerRow(doc: jsPDF, y: number, p: BusPassenger, idx: number): number {
  const H = 16;
  doc.setFillColor(idx % 2 === 0 ? 245 : 255, idx % 2 === 0 ? 248 : 255, 255);
  doc.rect(M, y, W - M * 2, H, 'F');
  const cols = [
    { v: p.name, x: M + 3, w: 52 },
    { v: p.cpf || '—', x: M + 57, w: 30 },
    { v: p.rg || '—', x: M + 89, w: 26 },
    { v: new Date(p.birth_date).toLocaleDateString('pt-BR'), x: M + 117, w: 26 },
    { v: p.phone_number || '—', x: M + 145, w: 35 },
  ];
  cols.forEach((c) => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...rgb(DARK));
    doc.text(doc.splitTextToSize(c.v, c.w)[0], c.x, y + 10);
  });
  return y + H;
}

function dashedLine(doc: jsPDF, y: number) {
  doc.setDrawColor(...rgb(BLUE));
  doc.setLineWidth(0.25);
  const seg = 3, gap = 2;
  let x = M;
  while (x < W - M) {
    doc.line(x, y, Math.min(x + seg, W - M), y);
    x += seg + gap;
  }
  doc.setFillColor(...rgb(BLUE));
  doc.circle(M - 3.5, y, 3.5, 'F');
  doc.circle(W - M + 3.5, y, 3.5, 'F');
}

export async function generateBusTicketPDF(checkout: BusCheckoutData) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = 0;

  const logoB64 = await loadLogoBase64();

  // ── Header da página ────────────────────────────────────────────────────
  doc.setFillColor(...rgb(DARK));
  doc.rect(0, 0, W, 28, 'F');
  doc.setFillColor(...rgb(BLUE));
  doc.rect(0, 26, W, 2, 'F');

  if (logoB64) {
    doc.addImage(logoB64, 'PNG', M, 7, 50, 9.5);
  } else {
    doc.setTextColor(...rgb(BLUE));
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('999 Viagens', M, 18);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('CARTÃO DE EMBARQUE', W - M, 13, { align: 'right' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(160, 200, 255);
  doc.text('Passagem de Ônibus  ·  999viagens.com.br', W - M, 20, { align: 'right' });

  y = 34;

  // ── Ticket IDA ──────────────────────────────────────────────────────────
  const valorIda = checkout.max_split_value_markup
    ? fmtCurrency(checkout.max_split_value_markup)
    : fmtCurrency(checkout.max_split_value);

  y = ticketBlock(doc, y, 'IDA', BLUE, logoB64, {
    cidadeOrigem: slugToCity(checkout.cidade_origem_slug),
    cidadeDestino: slugToCity(checkout.cidade_destino_slug),
    origemSlug: checkout.origem_slug || checkout.cidade_origem_slug,
    destinoSlug: checkout.destino_slug || checkout.cidade_destino_slug,
    partida: checkout.datetime_ida,
    chegada: checkout.chegada_ida,
    empresa: checkout.company_name,
    assento: checkout.tipo_assento,
    veiculo: checkout.tipo_veiculo,
    valor: valorIda,
    pedidoId: checkout.pedido_id,
    passageiro: checkout.name || '—',
  });

  // ── Ticket VOLTA ────────────────────────────────────────────────────────
  if (checkout.volta_trip_id) {
    y += 2;
    const valorVolta = checkout.volta_max_split_value_markup
      ? fmtCurrency(checkout.volta_max_split_value_markup)
      : fmtCurrency(checkout.volta_max_split_value);

    y = ticketBlock(doc, y, 'VOLTA', ORANGE, logoB64, {
      cidadeOrigem: slugToCity(checkout.volta_cidade_origin_slug),
      cidadeDestino: slugToCity(checkout.volta_cidade_destino_slug),
      origemSlug: checkout.volta_origem_slug || checkout.volta_cidade_origin_slug,
      destinoSlug: checkout.volta_destino_slug || checkout.volta_cidade_destino_slug,
      partida: checkout.volta_datetime_ida,
      chegada: checkout.volta_chegada_ida,
      empresa: checkout.volta_company_name,
      assento: checkout.volta_tipo_assento,
      veiculo: checkout.volta_tipo_veiculo,
      valor: valorVolta,
      pedidoId: checkout.pedido_id,
      passageiro: checkout.name || '—',
    });
  }

  y += 6;

  // ── Separador ───────────────────────────────────────────────────────────
  dashedLine(doc, y);
  y += 10;

  // ── Passageiros ─────────────────────────────────────────────────────────
  if (checkout.passengers && checkout.passengers.length > 0) {
    doc.setFillColor(...rgb(LIGHT_GRAY));
    doc.roundedRect(M, y, W - M * 2, 8, 2, 2, 'F');
    doc.setFillColor(...rgb(BLUE));
    doc.roundedRect(M, y, 4, 8, 2, 2, 'F');
    doc.rect(M + 2, y, 2, 8, 'F');
    doc.setTextColor(...rgb(DARK));
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('PASSAGEIROS', M + 8, y + 5.5);
    y += 10;

    const headers = [
      { t: 'Nome', x: M + 3 }, { t: 'CPF', x: M + 57 }, { t: 'RG', x: M + 89 },
      { t: 'Nascimento', x: M + 117 }, { t: 'Telefone', x: M + 145 },
    ];
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...rgb(GRAY));
    headers.forEach((h) => doc.text(h.t, h.x, y + 4));
    doc.setDrawColor(220, 232, 248);
    doc.setLineWidth(0.2);
    doc.line(M, y + 6, W - M, y + 6);
    y += 8;
    checkout.passengers.forEach((p, i) => { y = passengerRow(doc, y, p, i); });
    y += 4;
  }

  // ── Contato / Emissão ───────────────────────────────────────────────────
  dashedLine(doc, y);
  y += 10;

  doc.setFillColor(...rgb(LIGHT_GRAY));
  doc.roundedRect(M, y, W - M * 2, 18, 2, 2, 'F');

  [
    { label: 'PASSAGEIRO PRINCIPAL', value: checkout.name || '—', x: M + 4 },
    { label: 'E-MAIL', value: checkout.email || '—', x: M + 68 },
    { label: 'EMITIDO EM', value: fmtDate(checkout.created_at), x: M + 136 },
  ].forEach((c) => {
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...rgb(GRAY));
    doc.text(c.label, c.x, y + 5);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...rgb(DARK));
    doc.text(c.value, c.x, y + 13);
  });

  // ── Footer ──────────────────────────────────────────────────────────────
  const pageH = 297;
  doc.setFillColor(...rgb(DARK));
  doc.rect(0, pageH - 14, W, 14, 'F');
  doc.setFillColor(...rgb(BLUE));
  doc.rect(0, pageH - 14, W, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text('999 Viagens', W / 2, pageH - 7.5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(160, 200, 255);
  doc.setFontSize(6.5);
  doc.text('Apresente este documento no embarque com documento de identidade com foto.', W / 2, pageH - 3, { align: 'center' });

  doc.save(`ticket-onibus-${checkout.pedido_id}-${Date.now()}.pdf`);
}
